import React from 'react';
import { Box, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DesignServicesRoundedIcon from '@mui/icons-material/DesignServicesRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import { servicePages } from '../../servicePagesData';

const pillars = [
	{
		icon: <HubRoundedIcon />,
		title: 'Connected operations',
		description: 'We bring software, infrastructure, and workflow automation into one system your team can actually manage.',
	},
	{
		icon: <DesignServicesRoundedIcon />,
		title: 'Clear user experience',
		description: 'Every product decision is shaped around practical usage, not visual noise or unnecessary technical overhead.',
	},
	{
		icon: <VerifiedRoundedIcon />,
		title: 'Built to hold up',
		description: 'Security, maintainability, and delivery quality stay in scope from architecture through deployment.',
	},
];

const outcomes = [
	'Reduce manual work across fragmented tools and handoffs.',
	'Introduce better observability and more dependable release workflows.',
	'Modernize client-facing experiences without losing the value in existing systems.',
];

const features = [
	{
		title: 'Strategy that turns into shipping',
		description: 'We do the thinking and the execution, so plans become stable systems rather than unfinished recommendations.',
	},
	{
		title: 'Senior-level technical judgment',
		description: 'Architecture, DevOps, integrations, and product quality are handled as one connected problem.',
	},
	{
		title: 'Professional client experience',
		description: 'Communication is direct, delivery is structured, and every engagement is built to create trust.',
	},
];

const Home = () => {
	return (
		<Box id="home" sx={{ py: { xs: 10, md: 14 } }}>
			<Container maxWidth="xl">
				<Grid container spacing={{ xs: 4, md: 5 }} alignItems="stretch">
					<Grid item xs={12} lg={5}>
						<Typography variant="overline" sx={{ color: '#99f6e4', letterSpacing: '0.12em' }}>
							Why teams hire us
						</Typography>
						<Typography variant="h2" component="h2" sx={{ mt: 1.5, mb: 2.5, maxWidth: '11ch' }}>
							Modern product thinking with real engineering depth.
						</Typography>
						<Typography variant="body1" sx={{ maxWidth: 620, mb: 4 }}>
							BrokenCosmos Labs works best with companies that need more than a redesign. We help teams improve the way
							their platforms behave, the way their tooling connects, and the way their customers experience the product.
						</Typography>
						<Stack spacing={2.1}>
							{outcomes.map((item) => (
								<Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
									<CheckCircleOutlineIcon sx={{ mt: 0.3, color: '#67e8f9' }} />
									<Typography variant="body2" sx={{ color: 'text.primary' }}>
										{item}
									</Typography>
								</Box>
							))}
						</Stack>
						<Box sx={{ mt: 3.5, display: 'flex', flexWrap: 'wrap', gap: 1.3 }}>
							{servicePages.map((service) => (
								<Typography
									key={service.key}
									component={Link}
									to={service.path}
									variant="body2"
									sx={{
										px: 1.4,
										py: 0.8,
										borderRadius: '999px',
										backgroundColor: alpha('#0f172a', 0.6),
										border: `1px solid ${alpha('#e2e8f0', 0.08)}`,
										color: '#bfdbfe',
										'&:hover': { color: '#dbeafe' },
									}}
								>
									{service.kicker}
								</Typography>
							))}
						</Box>
					</Grid>

					<Grid item xs={12} lg={7}>
						<Grid container spacing={2.5}>
							{pillars.map((pillar) => (
								<Grid item xs={12} md={4} key={pillar.title}>
									<Paper
										elevation={0}
										sx={{
											height: '100%',
											p: 3,
											borderRadius: '32px',
											backgroundColor: alpha('#0f172a', 0.68),
										}}
									>
										<Box
											sx={{
												width: 48,
												height: 48,
												borderRadius: '16px',
												display: 'grid',
												placeItems: 'center',
												mb: 2,
												backgroundColor: alpha('#2563eb', 0.14),
												color: '#93c5fd',
											}}
										>
											{pillar.icon}
										</Box>
										<Typography variant="h5" sx={{ mb: 1.2 }}>
											{pillar.title}
										</Typography>
										<Typography variant="body2">{pillar.description}</Typography>
									</Paper>
								</Grid>
							))}
							<Grid item xs={12}>
								<Paper
									elevation={0}
									sx={{
										p: { xs: 3, md: 4 },
										borderRadius: '36px',
										background: 'linear-gradient(135deg, rgba(37,99,235,0.16) 0%, rgba(8,16,29,0.8) 55%)',
									}}
								>
									<Grid container spacing={3}>
										{features.map((feature) => (
											<Grid item xs={12} md={4} key={feature.title}>
												<Typography variant="h6" sx={{ mb: 1.2 }}>
													{feature.title}
												</Typography>
												<Typography variant="body2">{feature.description}</Typography>
											</Grid>
										))}
									</Grid>
								</Paper>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Container>
		</Box>
	);
};

const WhyChooseUsSection = () => (
	<Box sx={{ py: { xs: 10, md: 14 } }}>
		<Container maxWidth="xl">
			<Typography variant="h2" component="h2" sx={{ textAlign: 'center', mb: 2 }}>
				A sharper partner for product and infrastructure work
			</Typography>
			<Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: 7 }}>
				The goal is not to add more technology for its own sake. The goal is to make your systems easier to trust,
				easier to operate, and more impressive to the people using them.
			</Typography>
			<Grid container spacing={3}>
				{features.map((feature) => (
					<Grid item xs={12} md={4} key={feature.title}>
						<Paper
							elevation={0}
							sx={{
								p: 4,
								height: '100%',
								borderRadius: '32px',
								backgroundColor: alpha('#0b1729', 0.7),
							}}
						>
							<CheckCircleOutlineIcon sx={{ fontSize: 34, color: '#67e8f9', mb: 2.5 }} />
							<Typography variant="h5" component="h3" sx={{ mb: 1.5 }}>
								{feature.title}
							</Typography>
							<Typography variant="body2">{feature.description}</Typography>
						</Paper>
					</Grid>
				))}
			</Grid>
		</Container>
	</Box>
);

const HomePage = () => (
	<>
		<Home />
		<WhyChooseUsSection />
	</>
);

export default HomePage;
